import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/lib/api-client';

interface OnboardingData {
  logo?: File;
  timezone: string;
  language: string;
  dateFormat: string;
}

const TIMEZONES = [
  { value: 'America/Sao_Paulo', label: 'Brasília (GMT-3)' },
  { value: 'America/Manaus', label: 'Manaus (GMT-4)' },
  { value: 'America/Fortaleza', label: 'Fortaleza (GMT-3)' },
  { value: 'America/Recife', label: 'Recife (GMT-3)' },
];

const LANGUAGES = [
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'es-ES', label: 'Español' },
];

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' },
];

export default function OnboardingWizardPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    timezone: 'America/Sao_Paulo',
    language: 'pt-BR',
    dateFormat: 'DD/MM/YYYY',
  });

  const totalSteps = 3;

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Apenas arquivos PNG ou JPEG são permitidos');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('O arquivo deve ter no máximo 2MB');
      return;
    }

    setError('');
    setOnboardingData(prev => ({ ...prev, logo: file }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Step 1: Upload logo if provided
      if (onboardingData.logo) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(onboardingData.logo!);
        });

        await apiClient.post('/organizations/me/logo', {
          image: base64,
          contentType: onboardingData.logo.type,
        });
      }

      // Step 2: Update organization settings
      await apiClient.put('/organizations/me', {
        timezone: onboardingData.timezone,
        language: onboardingData.language,
        dateFormat: onboardingData.dateFormat,
      });

      // Step 3: Mark onboarding as complete
      await apiClient.post('/organizations/me/complete-onboarding', {});

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Onboarding error:', err);
      setError(err.message || 'Erro ao completar configuração. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold mb-2">Logo da Empresa</h3>
              <p className="text-sm text-muted-foreground">
                Adicione o logo da sua empresa (opcional)
              </p>
            </div>

            <div className="flex flex-col items-center space-y-4">
              {logoPreview ? (
                <div className="relative">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="w-32 h-32 object-contain rounded-lg border-2 border-gray-200"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      setLogoPreview(null);
                      setOnboardingData(prev => ({ ...prev, logo: undefined }));
                    }}
                  >
                    Remover
                  </Button>
                </div>
              ) : (
                <div className="w-full">
                  <Label htmlFor="logo" className="cursor-pointer">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors">
                      <div className="space-y-2">
                        <div className="text-4xl">📷</div>
                        <div className="text-sm text-muted-foreground">
                          Clique para selecionar ou arraste o arquivo
                        </div>
                        <div className="text-xs text-muted-foreground">
                          PNG ou JPEG, máximo 2MB
                        </div>
                      </div>
                    </div>
                  </Label>
                  <Input
                    id="logo"
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                </div>
              )}
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold mb-2">Configurações Regionais</h3>
              <p className="text-sm text-muted-foreground">
                Configure o fuso horário, idioma e formato de data
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="timezone">Fuso Horário</Label>
                <select
                  id="timezone"
                  value={onboardingData.timezone}
                  onChange={(e) => setOnboardingData(prev => ({ ...prev, timezone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {TIMEZONES.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="language">Idioma</Label>
                <select
                  id="language"
                  value={onboardingData.language}
                  onChange={(e) => setOnboardingData(prev => ({ ...prev, language: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dateFormat">Formato de Data</Label>
                <select
                  id="dateFormat"
                  value={onboardingData.dateFormat}
                  onChange={(e) => setOnboardingData(prev => ({ ...prev, dateFormat: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {DATE_FORMATS.map(format => (
                    <option key={format.value} value={format.value}>
                      {format.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold mb-2">Revisar e Completar</h3>
              <p className="text-sm text-muted-foreground">
                Confirme suas configurações antes de começar
              </p>
            </div>

            <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
              {logoPreview && (
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700">Logo:</span>
                  <img
                    src={logoPreview}
                    alt="Logo"
                    className="w-16 h-16 object-contain rounded border"
                  />
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-700">Fuso Horário:</span>
                <span className="text-sm text-gray-600">
                  {TIMEZONES.find(tz => tz.value === onboardingData.timezone)?.label}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-700">Idioma:</span>
                <span className="text-sm text-gray-600">
                  {LANGUAGES.find(lang => lang.value === onboardingData.language)?.label}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-700">Formato de Data:</span>
                <span className="text-sm text-gray-600">
                  {DATE_FORMATS.find(format => format.value === onboardingData.dateFormat)?.label}
                </span>
              </div>
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Você pode alterar essas configurações a qualquer momento nas configurações da organização.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="space-y-1">
          <CardTitle className="text-3xl font-bold text-center">
            Bem-vindo ao TalentumIQ
          </CardTitle>
          <CardDescription className="text-center">
            Configure sua organização em {totalSteps} passos simples
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
                <div key={step} className="flex items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      step === currentStep
                        ? 'bg-primary text-white'
                        : step < currentStep
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {step < currentStep ? '✓' : step}
                  </div>
                  {step < totalSteps && (
                    <div
                      className={`flex-1 h-1 mx-2 ${
                        step < currentStep ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>Logo</span>
              <span>Configurações</span>
              <span>Revisar</span>
            </div>
          </div>

          {/* Step content */}
          <div className="min-h-[300px]">
            {renderStepContent()}
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1 || isLoading}
            >
              Voltar
            </Button>

            {currentStep < totalSteps ? (
              <Button onClick={handleNext} disabled={isLoading}>
                Próximo
              </Button>
            ) : (
              <Button onClick={handleComplete} disabled={isLoading}>
                {isLoading ? 'Finalizando...' : 'Completar Configuração'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
